import { ColumnSettings } from './excel.generator';
import { ExcelCellType, FormattedCell } from '../../models/report';

interface ExcelCellData {
  v: any;
  t: string;
  z?: string;
  w?: string;
}

const DATE_FORMAT = 'm/d/yy';
const NUMBER_FORMAT = '#,##0';
const SUBSCRIPTION_FORMAT = '0.0';
const COUPON_PERCENTS = '0.000%';
const COLUMN_MARGIN = 3;
const DATE_COLUMN_WIDTH = 12;

export function toMillion(
  objToFormat: any | any[],
  except: string[] = []
): any | any[] {
  const list = [].concat(objToFormat) as any;
  const isSingle = !Array.isArray(objToFormat);
  const result = [];

  list.forEach((item, index) => {
    const newItem = Object.keys(item).reduce((prev, nextProp) => {
      const [ignored] = except.filter(exceptProp => exceptProp === nextProp);

      if (typeof item[nextProp] === 'number' && !ignored) {
        prev[nextProp] = item[nextProp] / 1000;
      } else {
        prev[nextProp] = item[nextProp];
      }

      return prev;
    }, {});

    result.push(newItem);
  });

  if (isSingle) {
    return result[0];
  }

  return result;
}

export function getColumnSettings(
  value: any,
  index: number,
  columnSettings: ColumnSettings
): ColumnSettings {
  const { width } = columnSettings || { width: 0 };
  const colWidth = !(value instanceof Date)
    ? String(value).length
    : DATE_COLUMN_WIDTH;

  if (width < colWidth) {
    return { width: colWidth + COLUMN_MARGIN } as ColumnSettings;
  }

  return columnSettings;
}

export function getFormattedCell(field: string, value: any): FormattedCell {
  if (field === 'coupon' && (value || value === 0)) {
    return getFormattedCouponCell(value);
  } else if (field === 'subscription' && (value || value === 0)) {
    return getFormattedSubscriptionCell(value);
  }

  return { value: getValueOrEmptyString(value), format: undefined };
}

export function valueToCell({ value, format }: FormattedCell): ExcelCellData {
  if (value && format) {
    return { v: value as any, t: format.t, z: format.z };
  }

  return valueToCellDefault(value);
}

function getFormattedCouponCell(value: number): FormattedCell {
  if (value === 0) {
    return {
      value: Number(value).toFixed(3),
      format: { t: 'n', z: COUPON_PERCENTS }
    };
  }

  return {
    value: getValueOrEmptyString(value / 100),
    format: { t: 'n', z: COUPON_PERCENTS }
  };
}

function getFormattedSubscriptionCell(value: number): FormattedCell {
  if (value > 1) {
    return {
      value,
      format: { t: 'n', z: SUBSCRIPTION_FORMAT }
    };
  } else if (value === 0) {
    return {
      value: Number(value).toFixed(1),
      format: { t: 'n', z: SUBSCRIPTION_FORMAT }
    };
  }

  return {
    value: getValueOrEmptyString(Number(value).toFixed(1)),
    format: { t: 'n', z: SUBSCRIPTION_FORMAT }
  };
}

function getValueOrEmptyString(value: any): any {
  if (value === undefined || value === null) {
    return '';
  }

  return value;
}

function valueToCellDefault(value: any): ExcelCellData {
  let type: ExcelCellType = 's';
  let format: string;

  if (value instanceof Date) {
    type = 'd';
    // compensate UTC offset
    const offset = new Date().getTimezoneOffset();
    value = new Date(value.getTime() + (offset * 60 * 1000));

    return {
      t: type,
      v: value as any,
      w: value.toISOString(),
      z: DATE_FORMAT
    };
  }
  if (value === null || value === undefined) {
    // tslint:disable-line:no-null-keyword
    return {
      t: type,
      v: ''
    };
  }

  switch (typeof value) {
    case 'boolean':
      type = 'b';
      break;
    case 'number':
      type = 'n';
      format = NUMBER_FORMAT;
      break;
    default:
      type = 's';
      break;
  }

  return { t: type, z: format, v: value };
}
